import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { NavigationExtras, Router } from '@angular/router';
import { assertExists } from 'common';
import { NGXLogger } from 'ngx-logger';
import { encodeBase64 } from 'src/app/common/utility';
import { SearchesQuery } from 'src/app/state/searches/searches.query';

/** Describes the data that needs to be passed to the merge document success dialog. */
export interface MergeDocumentsResultDialogData {
  /** Document ID for the merged document. */
  documentId: number;
}

/** Describes the result from the merge documents success dialog. */
export interface MergeDocumentDialogResult {
  deleteSourceDocuments: boolean;
}

/** Describes the reference type for the merge document success dialog. */
export type MergeDocumentsResultDialogRef = MatDialogRef<
  MergeDocumentsResultDialogComponent,
  MergeDocumentDialogResult
>;

@Component({
  selector: 'app-merge-documents-result-dialog',
  templateUrl: './merge-documents-result-dialog.component.html',
  styleUrls: ['./merge-documents-result-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MergeDocumentsResultDialogComponent {
  private dialogData: MergeDocumentsResultDialogData = inject(MAT_DIALOG_DATA);
  private dialog: MergeDocumentsResultDialogRef = inject(MatDialogRef);
  private logger = inject(NGXLogger);
  private router = inject(Router);
  private searchesQuery = inject(SearchesQuery);

  private archiveId = computed(() => this.searchRouteParams()?.[1]);
  private databaseId = computed(() => this.searchRouteParams()?.[0]);
  private searchId = computed(() => this.searchRouteParams()?.[2]);
  private searchRouteParams = toSignal(this.searchesQuery.searchRouteParams$);
  private searchRoutePrompts = toSignal(this.searchesQuery.searchPromptParams$);

  /**
   * Opens the dialog.
   *
   * @param documentId Document ID for the merged document.
   * @param dialogService MatDialog service reference to open the dialog.
   * @returns A reference to the open dialog.
   */
  static openDialog(
    documentId: number,
    dialogService: MatDialog
  ): MergeDocumentsResultDialogRef {
    return dialogService.open<
      MergeDocumentsResultDialogComponent,
      MergeDocumentsResultDialogData,
      MergeDocumentDialogResult
    >(MergeDocumentsResultDialogComponent, {
      data: {
        documentId,
      },
    });
  }

  /** Handler for the close button click event. */
  onClickClose(): void {
    this.dialog.close({ deleteSourceDocuments: false });
  }

  /** Handler for the delete button click event. */
  onClickDelete(): void {
    this.dialog.close({ deleteSourceDocuments: true });
  }

  /** Handler for the open button click event. */
  onClickOpenDocument(): void {
    this.logger.debug(
      'Opening document in a new tab.',
      this.dialogData.documentId
    );
    const databaseId = this.databaseId();
    const archiveId = this.archiveId();
    const searchId = this.searchId();
    assertExists(databaseId);
    assertExists(archiveId);
    assertExists(searchId);
    const navigationExtras: NavigationExtras | undefined =
      (this.searchRoutePrompts()?.length ?? 0) > 0
        ? {
            queryParams: {
              prompts: encodeBase64(JSON.stringify(this.searchRoutePrompts())),
            },
            queryParamsHandling: 'merge',
          }
        : undefined;

    const urlTree = this.router.createUrlTree(
      [
        'db',
        databaseId,
        'archive',
        archiveId,
        'search',
        searchId,
        'document',
        this.dialogData.documentId,
      ],
      navigationExtras
    );

    window.open(urlTree.toString(), '_blank');
  }
}
